import React, { useEffect, useMemo, useState } from 'react';
import { buildAssetUrl, employeeAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Badge } from './ui/badge';
import { format } from 'date-fns';
import { Input } from './ui/input';
import { BarChart3, Search, Users } from 'lucide-react';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { chartColor, tooltipContentStyle, tooltipCursor, axisProps, gridProps } from '../lib/chartTheme';
import { EmptyState } from './common/States';
import { StatCard } from './common/StatCard';

const TeamSection = () => {
  const [team, setTeam] = useState([]);
  const [hierarchy, setHierarchy] = useState([]);
  const [offThisWeek, setOffThisWeek] = useState([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [teamRes, hierarchyRes, offThisWeekRes] = await Promise.all([
          employeeAPI.getMyTeam(),
          employeeAPI.getMyHierarchy(),
          employeeAPI.getCoworkersOffThisWeek()
        ]);
        setTeam(teamRes.data);
        setHierarchy(hierarchyRes.data);
        setOffThisWeek(offThisWeekRes.data);
      } catch (error) {
        console.error("Error fetching team data", error);
      }
    };
    fetchData();
  }, []);

  const filteredTeam = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return team;
    return team.filter((member) =>
      `${member.name || ''} ${member.designation || ''}`.toLowerCase().includes(query)
    );
  }, [team, search]);

  const leaveTypeSplit = useMemo(() => {
    const counts = offThisWeek.reduce((acc, item) => {
      const key = (item.leave_type || 'other').toLowerCase();
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(counts).map(([name, value], idx) => ({
      name: name.replace('_', ' '),
      value,
      color: chartColor(idx),
    }));
  }, [offThisWeek]);

  const hierarchyChartData = useMemo(
    () =>
      hierarchy.map((person, index) => ({
        level: person.level || `L${index + 1}`,
        order: index + 1,
      })),
    [hierarchy]
  );

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="grid gap-4 md:grid-cols-3">
        <StatCard label="Team Members" value={team.length} icon={Users} tone="primary" />
        <StatCard label="Off This Week" value={offThisWeek.length} icon={BarChart3} tone="info" />
        <StatCard label="Hierarchy Levels" value={hierarchy.length} icon={BarChart3} tone="warning" />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="border-border shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-primary" />
              Leave Type Split (This Week)
            </CardTitle>
          </CardHeader>
          <CardContent className="h-[260px]">
            {leaveTypeSplit.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={leaveTypeSplit}
                    dataKey="value"
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={88}
                  >
                    {leaveTypeSplit.map((entry, idx) => (
                      <Cell key={entry.name} fill={chartColor(idx)} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipContentStyle} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-full items-center justify-center">
                <EmptyState compact title="Nobody off this week" description="Teammates on leave will appear here." icon={Users} />
              </div>
            )}
          </CardContent>
        </Card>
        <Card className="border-border shadow-sm">
          <CardHeader>
            <CardTitle>Hierarchy Depth</CardTitle>
          </CardHeader>
          <CardContent className="h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={hierarchyChartData}>
                <CartesianGrid {...gridProps} />
                <XAxis dataKey="level" {...axisProps} />
                <YAxis allowDecimals={false} {...axisProps} />
                <Tooltip contentStyle={tooltipContentStyle} cursor={tooltipCursor} />
                <Bar dataKey="order" fill={chartColor(1)} radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Hierarchy Section */}
      {hierarchy.length > 0 && (
        <Card className="hover:shadow-md transition-all duration-300 border-border animate-slide-up">
          <CardHeader><CardTitle>Reporting Hierarchy</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-4 relative before:absolute before:left-6 before:top-6 before:h-full before:w-0.5 before:bg-border">
              {hierarchy.map((manager, index) => (
                <div key={index} className="flex items-center gap-4 relative pl-12">
                   {/* Connector Line */}
                   <div className="absolute left-6 top-1/2 w-6 h-0.5 bg-border"></div>
                   
                  <Avatar className="h-12 w-12 border-2 border-background shadow-sm">
                    <AvatarImage src={manager.photo} />
                    <AvatarFallback>{manager.name.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">{manager.name}</p>
                    <p className="text-hr-sm text-muted-foreground">{manager.designation}</p>
                    <Badge variant="outline" className="mt-1 text-hr-xs">{manager.level}</Badge>
                  </div>
                </div>
              ))}
              {/* Current User (You) */}
              <div className="flex items-center gap-4 relative pl-12 pt-4">
                 <div className="absolute left-6 top-1/2 w-6 h-0.5 bg-border"></div>
                 <div className="h-3 w-3 rounded-full bg-primary absolute left-[22px] top-[calc(50%-6px)] border-2 border-background ring-1 ring-primary"></div>
                 <span className="text-hr-sm font-medium text-primary bg-primary/10 px-3 py-1 rounded-full">You</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div>
        <h3 className="text-hr-lg font-semibold mb-4">Co-Workers Off This Week</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {offThisWeek.map((leave) => (
            <Card key={leave.leave_id} className="border-border">
              <CardContent className="p-5 flex items-start gap-4">
                <Avatar className="h-12 w-12 border">
                  <AvatarImage src={buildAssetUrl(leave.profile_photo_url)} />
                  <AvatarFallback>{leave.employee_name.charAt(0)}</AvatarFallback>
                </Avatar>
                <div className="space-y-2">
                  <div>
                    <p className="font-semibold text-foreground">{leave.employee_name}</p>
                    <p className="text-hr-sm text-muted-foreground">{leave.designation || 'Team Member'}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="secondary" className="capitalize">{leave.leave_type} leave</Badge>
                    <Badge variant="outline">
                      {format(new Date(leave.start_date), 'MMM dd')} - {format(new Date(leave.end_date), 'MMM dd')}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          {offThisWeek.length === 0 && (
            <div className="col-span-full">
              <EmptyState compact title="Nobody off this week" description="No co-workers are marked off this week." icon={Users} />
            </div>
          )}
        </div>
      </div>

      {/* Team Members Grid */}
      <div>
        <div className="mb-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <h3 className="text-hr-lg font-semibold">My Department Team</h3>
          <div className="relative w-full md:w-[320px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or designation"
              className="pl-9"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredTeam.map((member, index) => (
            <Card key={member.employee_id} className="hover-lift hover:shadow-lg transition-all duration-200 border-border" style={{ animationDelay: `${index * 0.05}s` }}>
              <CardContent className="p-6 flex flex-col items-center text-center space-y-3">
                <Avatar className="h-20 w-20">
                  <AvatarImage src={buildAssetUrl(member.profile_photo_url)} />
                  <AvatarFallback className="text-hr-lg bg-muted">{member.name.charAt(0)}</AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-semibold text-foreground">{member.name}</p>
                  <p className="text-hr-sm text-muted-foreground">{member.designation}</p>
                </div>
                <Badge variant="outline" className="inline-flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  Teammate
                </Badge>
              </CardContent>
            </Card>
          ))}
          {filteredTeam.length === 0 && (
            <div className="col-span-full">
              <EmptyState compact title="No team members found" description="No matching team members found." icon={Users} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamSection;
